import React, { useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Section = ({ title, children }) => (
  <div className="mb-8">
    {title && (
      <h2 className="font-display text-white text-xl sm:text-2xl uppercase tracking-wide mt-8 mb-3">
        {title}
      </h2>
    )}
    <div className="text-gray-300 text-[15px] leading-relaxed space-y-4">{children}</div>
  </div>
);

const List = ({ items }) => (
  <ul className="list-disc pl-5 space-y-2">
    {items.map((t, i) => (
      <li key={i}>{t}</li>
    ))}
  </ul>
);

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black" data-testid="privacy-policy-page">
      <Header />
      <main className="pt-28 pb-20 px-6">
        <div className="container mx-auto max-w-3xl">
          <p className="text-gray-500 uppercase text-xs tracking-[0.3em] mb-3">The Dillon Blueprint</p>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl uppercase leading-none">
            Privacy Policy
          </h1>
          <p className="text-gray-500 text-sm mt-4">
            Effective date: August 30, 2026 | thedillonblueprint.com/privacy-policy
          </p>
          <div className="h-px bg-neutral-800 my-8" />

          <Section>
            <p>
              This Privacy Policy explains how Alou Dillon d/b/a The Dillon Blueprint (“The Dillon
              Blueprint,” “we,” “us,” or “our”) collects, uses, discloses, and protects personal
              information when you visit thedillonblueprint.com, submit a form, communicate with us,
              purchase products or services, or participate in our programs. By using our website or
              services, you acknowledge the practices described in this policy.
            </p>
          </Section>

          <Section title="1. Information We Collect">
            <p>We may collect the following categories of information, depending on how you interact with us:</p>
            <List
              items={[
                "Contact and identity information, such as an athlete's name, age or age range, parent or guardian name, email address, mobile number, city, and account or application information.",
                'Training and program information, such as basketball or fitness experience, goals, availability, attendance, package use, progress, performance information, and program preferences.',
                'Health and safety information that you choose to provide or that we reasonably request to deliver training safely, such as injuries, limitations, medical clearances, nutrition information, or relevant fitness history.',
                'Payment and transaction information, such as purchases, subscription status, invoices, refunds, credits, shipping details, and payment confirmations. Payment processors generally collect full payment-card details directly; we may receive limited transaction information rather than the complete card number.',
                'Communications and consent records, including emails, text messages, calls, support requests, form submissions, SMS opt-in status, opt-out requests, and the date, time, and source of consent.',
                'Photos, videos, audio, testimonials, player statistics, and training results when you or a parent or guardian provides them or authorizes their collection or use.',
                'Website and device information, such as IP address, browser type, device type, referring page, pages viewed, approximate location, and information collected through cookies, pixels, analytics, server logs, or similar technologies.',
              ]}
            />
          </Section>

          <Section title="2. How We Collect Information">
            <p>
              We collect information directly from you, from a parent or guardian, automatically through
              our website and related technologies, and from service providers that help us operate the
              business. We may also receive information from schools, teams, coaches, referral partners,
              video platforms, or other people when they are authorized to provide it.
            </p>
          </Section>

          <Section title="3. How We Use Information">
            <List
              items={[
                'Review applications and determine whether a program may be a good fit.',
                'Schedule, deliver, personalize, and administer basketball training, fitness coaching, film mentorship, online programs, apparel orders, and related services.',
                'Communicate about applications, scheduling, sessions, training updates, purchases, customer service, safety, and account matters.',
                'Process payments, track packages or subscriptions, prevent duplicate or fraudulent transactions, and maintain business records.',
                'Monitor progress, improve programming, operate the website, troubleshoot problems, and understand how visitors use our services.',
                'Send promotional emails or marketing text messages only when legally permitted and, where required, after obtaining separate consent.',
                'Protect participants, clients, our business, and others; enforce agreements; respond to legal requests; and comply with applicable law.',
              ]}
            />
          </Section>

          <Section title="4. SMS Communications and Mobile Information">
            <p>
              If you provide a mobile number and select an optional SMS consent checkbox, The Dillon
              Blueprint may send non-marketing text messages about your application, scheduling, training
              sessions, and training updates. Message frequency varies. Message and data rates may apply.
              Reply STOP to opt out or HELP for help. Consent to receive text messages is not required to
              submit an application or purchase a service. Marketing text messages, if offered, require a
              separate consent where required by law.
            </p>
            <p>
              No mobile information will be shared with third parties/affiliates for marketing/promotional
              purposes. Information sharing to subcontractors in support services, such as customer
              service, is permitted. All other use case categories exclude text messaging originator
              opt-in data and consent; this information will not be shared with any third parties.
            </p>
            <p>
              We keep records of SMS consent and opt-out requests as reasonably necessary to honor your
              choices and meet legal or carrier requirements.
            </p>
          </Section>

          <Section title="5. How We Disclose Information">
            <p>We may disclose personal information in the following limited circumstances:</p>
            <List
              items={[
                'Service providers. We may use companies that provide website hosting, customer relationship management, scheduling, communications, payment processing, ecommerce, shipping, cloud storage, analytics, bookkeeping, and technical support. They may access information only as reasonably necessary to perform services for us and are expected to protect it.',
                'Professional and legal purposes. We may disclose information to attorneys, accountants, insurers, facilities, law enforcement, regulators, or other parties when reasonably necessary to protect safety, establish or defend legal claims, enforce agreements, or comply with law.',
                'Business changes. Information may be transferred as part of a merger, sale, financing, reorganization, or transfer of all or part of the business, subject to appropriate protections.',
                'At your direction or with consent. We may disclose information when you or an authorized parent or guardian asks us to do so or provides permission.',
              ]}
            />
            <p>
              We do not sell mobile numbers or SMS opt-in information. We do not sell personal information
              in exchange for money.
            </p>
          </Section>

          <Section title="6. Payments and Third-Party Services">
            <p>
              Payments, ecommerce transactions, scheduling, messaging, community access, and other
              functions may be handled by third-party platforms. Those providers process information
              under their own privacy notices and terms. We encourage you to review their policies. The
              Dillon Blueprint is not responsible for the independent privacy practices of third-party
              websites, applications, or services.
            </p>
          </Section>

          <Section title="7. Cookies and Similar Technologies">
            <p>
              Our website and service providers may use cookies, pixels, local storage, analytics, and
              similar technologies to keep the website functioning, remember preferences, measure
              traffic, improve performance, and understand marketing effectiveness. You can control some
              cookies through your browser or device settings. Blocking cookies may affect website
              features.
            </p>
          </Section>

          <Section title="8. Photos, Video, Statistics, and Testimonials">
            <p>
              We may collect training footage, game footage, photos, testimonials, player statistics, and
              progress results to deliver services or document progress. We will obtain appropriate
              authorization before using identifiable content publicly for advertising or promotional
              purposes. A parent or guardian must authorize public promotional use involving a minor. A
              separate media release may apply.
            </p>
          </Section>

          <Section title="9. Children and Teen Athletes">
            <p>
              Our website is not intended to collect personal information directly from children under
              13. A parent or guardian must submit information on behalf of a child under 13. If we learn
              that a child under 13 submitted personal information directly without appropriate parental
              authorization, we will take reasonable steps to delete or restrict it.
            </p>
            <p>
              Athletes under 18 may submit an initial application on their own. Once a parent or guardian
              completes the required one-time enrollment approval — this Privacy Policy, our Terms and
              Conditions, and our liability waiver — the athlete may communicate directly with us about
              scheduling and training. Parent or guardian approval remains required before payment,
              participation, and any public promotional use of the minor's image, voice, testimonial, or
              results. We may contact the parent or guardian to verify authorization.
            </p>
          </Section>

          <Section title="10. Data Retention">
            <p>
              We retain information for as long as reasonably necessary for the purposes described in
              this policy, including providing services, maintaining training and transaction records,
              honoring consent and opt-out choices, resolving disputes, protecting safety, and meeting
              legal, accounting, insurance, or contractual requirements. Retention periods vary by the
              type of information and relationship.
            </p>
          </Section>

          <Section title="11. Data Security">
            <p>
              We use reasonable administrative, technical, and physical safeguards appropriate to the
              information we maintain. No website, transmission method, or storage system is completely
              secure, so we cannot guarantee absolute security. Please avoid submitting highly sensitive
              medical or financial information through a general website form unless we specifically
              request it through an appropriate method.
            </p>
          </Section>

          <Section title="12. Your Choices and Requests">
            <p>
              Depending on your relationship with us and applicable law, you may ask to access, correct,
              or delete certain personal information. You may unsubscribe from marketing emails through
              the link in the email and opt out of text messages by replying STOP. You may also change
              browser cookie settings. We may need to verify your identity or a parent or guardian's
              authority before completing a request, and legal exceptions may apply.
            </p>
          </Section>

          <Section title="13. Third-Party Links">
            <p>
              Our website may link to outside websites, social media platforms, booking pages, payment
              pages, video platforms, or other services. This Privacy Policy does not control those third
              parties, and we are not responsible for their content or privacy practices.
            </p>
          </Section>

          <Section title="14. Changes to This Privacy Policy">
            <p>
              We may update this Privacy Policy as our services, technology, or legal obligations change.
              The revised version will be posted on this page with a new effective date. Material changes
              may be communicated through the website or another reasonable method.
            </p>
          </Section>

          <Section title="15. Contact Us">
            <p>For privacy questions or requests, contact:</p>
            <p className="not-italic">
              Alou Dillon d/b/a The Dillon Blueprint
              <br />
              Milwaukee, Wisconsin
              <br />
              Email:{' '}
              <a href="mailto:aloudillon@tdbtraining.com" className="text-white underline">
                aloudillon@tdbtraining.com
              </a>
            </p>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
